// Generator dispatch: spec.gen → generator. Every generator draws in cell units (100 = one cell) into a
// canvas of frameSize(spec, px) device pixels. A board item frame also carries its tier badge, drawn
// bottom-right at `badgeShare` of the cell (M5 decision 5: the badge is baked into the item frame).
import { stallBadgeGen, tierBadgeGen } from './badges.ts';
import type { Ctx2D } from './ctx.ts';
import { fxGen } from './fx.ts';
import { itemGen } from './items/index.ts';
import {
  buildingGen,
  chevronGen,
  chuteGen,
  depositGen,
  drillHeadGen,
  movingPartGen,
} from './machines/index.ts';
import { markerGen } from './markers.ts';
import { sargeGen } from './sarge.ts';
import { targetGen } from './targets.ts';
import { tileGen } from './tiles.ts';
import type { ArtEnv, FrameSpec, ItemSpec, TierBadgeSpec } from './types.ts';
import { uiIconGen } from './uiIcons.ts';

export function frameSize(spec: FrameSpec, px: number): [number, number] {
  return [Math.max(1, Math.round(spec.w * px)), Math.max(1, Math.round(spec.h * px))];
}

/** The tier badge an item frame carries (bottom-right corner, `badgeShare` of the cell). */
export function itemBadgeSpec(spec: ItemSpec): TierBadgeSpec {
  return {
    key: spec.badge,
    group: 'badge',
    version: spec.version,
    w: spec.badgeShare,
    h: spec.badgeShare,
    pivot: { x: 0.5, y: 0.5 },
    seed: spec.seed,
    atlas: false,
    gen: 'tierBadge',
    tier: spec.tier,
  };
}

export function drawFrame(c: Ctx2D, spec: FrameSpec, px: number, env: ArtEnv): void {
  c.save();
  switch (spec.gen) {
    case 'item':
      itemGen(c, spec, px, env);
      if (spec.badged) {
        const b = itemBadgeSpec(spec);
        const [fw, fh] = frameSize(spec, px);
        const [bw, bh] = frameSize(b, px);
        c.save();
        c.translate(fw - bw, fh - bh);
        tierBadgeGen(c, b, px, env);
        c.restore();
      }
      break;
    case 'building':
      buildingGen(c, spec, px, env);
      break;
    case 'deposit':
      depositGen(c, spec, px, env);
      break;
    case 'drillHead':
      drillHeadGen(c, spec, px, env);
      break;
    case 'chute':
      chuteGen(c, spec, px, env);
      break;
    case 'chevron':
      chevronGen(c, spec, px, env);
      break;
    case 'movingPart':
      movingPartGen(c, spec, px, env);
      break;
    case 'tile':
      tileGen(c, spec, px, env);
      break;
    case 'tierBadge':
      tierBadgeGen(c, spec, px, env);
      break;
    case 'stallBadge':
      stallBadgeGen(c, spec, px, env);
      break;
    case 'marker':
      markerGen(c, spec, px, env);
      break;
    case 'target':
      targetGen(c, spec, px, env);
      break;
    case 'sarge':
      sargeGen(c, spec, px, env);
      break;
    case 'uiIcon':
      uiIconGen(c, spec, px, env);
      break;
    case 'fx':
      fxGen(c, spec, px, env);
      break;
  }
  c.restore();
}
